import React, { useState, useEffect } from "react";
import { 
  Coins, 
  HeartHandshake, 
  Plus, 
  Download, 
  ArrowDownLeft, 
  ArrowUpRight, 
  Search, 
  Filter, 
  ShieldCheck, 
  Trash2,
  Pencil,
  Landmark,
  ArrowLeftRight,
  Tag,
  Upload,
  FileText,
  Building2,
  Receipt,
  Sparkles,
  Clock,
  CheckCircle2,
  Sliders,
  Printer,
  BadgeCheck,
  Gift,
  Crown,
  History,
  RotateCcw,
  FileSpreadsheet,
  Layers,
  AlertTriangle,
  UserCheck,
  Ban,
  BookOpen
} from "lucide-react";
import { InstallmentBatchForm } from "./InstallmentBatchForm";
import { BalancesByPersonView } from "./BalancesByPersonView";
import { FilantropiaReportView } from "./FilantropiaReportView";
import { RateioRulesCard } from "./RateioRulesCard";
import { toast } from "sonner";
import { EventsView } from "./EventsView";
import { FinanceChartOfAccounts } from "./FinanceChartOfAccounts";

export const LodgeFinance = ({
  transactions,
  members,
  contacts = [],
  stats,
  costCenters,
  bankAccounts = [],
  treasurySettings,
  onOpenNewFinanceModal,
  onOpenCostCenterModal,
  onEditCostCenter,
  onOpenBankAccountModal,
  onEditBankAccount,
  onDeleteBankAccount,
  onOpenTransferModal,
  onClassifyTx,
  onOpenImportStatement,
  onMarkPaid,
  onCancelPending,
  onEditPendingBank,
  onUpdateTreasurySettings,
  onDeleteCostCenter,
  onDeleteTransaction,
  onReverseTransaction,
  onCancelBatch,
  onViewMemberStatement,
  onViewReceipt,
  onMarkAsPaid,
  initialSubTab = "geral"
}) => {
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab);
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);
  const [accountTypeFilter, setAccountTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Settings inline edit
  const [dueDay, setDueDay] = useState(treasurySettings?.due_day || 10);
  const [discountVal, setDiscountVal] = useState(treasurySettings?.prompt_discount_value || 20.0);
  const [defaultFee, setDefaultFee] = useState(treasurySettings?.default_monthly_fee || 180.0);
  const [gestaoStartMonth, setGestaoStartMonth] = useState(treasurySettings?.gestao_start_month || 7);
  const [gestaoStartYear, setGestaoStartYear] = useState(treasurySettings?.gestao_start_year || new Date().getFullYear());
  const [emeritoDisc, setEmeritoDisc] = useState(treasurySettings?.emerito_discount_value || 0);
  const [provectoDisc, setProvectoDisc] = useState(treasurySettings?.provecto_discount_value || 0);
  const [emeritoEffFrom, setEmeritoEffFrom] = useState(treasurySettings?.emerito_effective_from || "");
  const [provectoEffFrom, setProvectoEffFrom] = useState(treasurySettings?.provecto_effective_from || "");
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [creditAlerts, setCreditAlerts] = useState(null);
  const [creditAlertDismissed, setCreditAlertDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const axios = (await import("axios")).default;
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${BACKEND_URL}/api/treasury/credit-alerts`);
        if (alive) setCreditAlerts(res.data);
      } catch { /* silent */ }
    };
    load();
    return () => { alive = false; };
  }, [transactions]);

  useEffect(() => {
    if (!showHistory) return;
    let alive = true;
    const load = async () => {
      setHistoryLoading(true);
      try {
        const axios = (await import("axios")).default;
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${BACKEND_URL}/api/treasury/settings/history`);
        if (alive) setHistory(res.data || []);
      } catch { /* silent */ }
      finally { if (alive) setHistoryLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [showHistory, treasurySettings]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    await onUpdateTreasurySettings({
      due_day: parseInt(dueDay, 10),
      prompt_discount_value: parseFloat(discountVal),
      default_monthly_fee: parseFloat(defaultFee),
      gestao_start_month: parseInt(gestaoStartMonth, 10),
      gestao_start_year: parseInt(gestaoStartYear, 10),
      emerito_discount_value: parseFloat(emeritoDisc) || 0,
      provecto_discount_value: parseFloat(provectoDisc) || 0,
      emerito_effective_from: emeritoEffFrom || null,
      provecto_effective_from: provectoEffFrom || null,
    });
    setIsEditingSettings(false);
    toast.success("Regras de vencimento, desconto e período de gestão atualizadas!");
  };

  const filteredTransactions = transactions.filter((tx) => {
    // Excluir pendentes de classificação (aba Pendentes → Seção 1)
    if (tx.needs_review) return false;
    // Excluir pendentes de pagamento (aba Pendentes → Seção 2). Só entram no extrato quando pagos/estornados
    if (tx.status === "pendente") return false;
    if (accountTypeFilter !== "all") {
      if (accountTypeFilter.startsWith("ba:")) {
        const baId = accountTypeFilter.slice(3);
        // Casa: bank_account_id === baId OU (é conta padrão E account_type da tx bate com o seed_key)
        const ba = bankAccounts.find(b => b.id === baId);
        const isDirect = tx.bank_account_id === baId;
        const isLegacy = ba?.seed_key && !tx.bank_account_id && tx.account_type === ba.seed_key;
        if (!isDirect && !isLegacy) return false;
      } else if (tx.account_type !== accountTypeFilter) {
        return false;
      }
    }
    // Filtro de datas — usa payment_date, senão created_at
    if (dateFrom || dateTo) {
      const txDate = (tx.payment_date || (tx.created_at || "").substring(0, 10));
      if (dateFrom && txDate < dateFrom) return false;
      if (dateTo && txDate > dateTo) return false;
    }
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      const matchDesc = tx.description?.toLowerCase().includes(s);
      const matchCat = tx.category?.toLowerCase().includes(s);
      const matchProof = tx.proof_code?.toLowerCase().includes(s);
      return matchDesc || matchCat || matchProof;
    }
    return true;
  });

  const pendingClassifyList = transactions.filter(t => t.needs_review);
  const pendingPaymentList = transactions.filter(t => !t.needs_review && t.status === "pendente" && !t.reversed_by);
  const overdueCount = pendingPaymentList.filter(t => {
    const d = t.due_date || t.payment_date;
    if (!d) return false;
    return d < new Date().toISOString().slice(0,10);
  }).length;
  const pendingCount = pendingClassifyList.length + pendingPaymentList.length;

  const todayStr = new Date().toISOString().slice(0, 10);
  const in7Days = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const getDueStatus = (tx) => {
    const due = tx.due_date || tx.payment_date;
    if (!due) return { level: "neutral", label: "Sem vencimento" };
    if (due < todayStr) {
      const days = Math.floor((new Date(todayStr) - new Date(due)) / (24 * 60 * 60 * 1000));
      return { level: "overdue", label: `Vencido há ${days} dia${days > 1 ? "s" : ""}` };
    }
    if (due <= in7Days) return { level: "soon", label: `Vence em breve (${new Date(due + "T00:00").toLocaleDateString("pt-BR")})` };
    return { level: "future", label: `Vence ${new Date(due + "T00:00").toLocaleDateString("pt-BR")}` };
  };

  const getTransactionPerson = (tx) => {
    if (tx.member_id) {
      const member = (members || []).find((item) => item.id === tx.member_id);
      return member ? { name: member.name, detail: `Irmão • CIM ${member.cim || "—"}` } : { name: "Irmão não encontrado", detail: tx.member_id };
    }
    if (tx.contact_id) {
      const contact = (contacts || []).find((item) => item.id === tx.contact_id);
      const kind = contact?.kind === "fornecedor" ? "Fornecedor" : contact?.kind === "cliente" ? "Cliente" : "Contato";
      return contact ? { name: contact.name, detail: kind } : { name: "Contato não encontrado", detail: tx.contact_id };
    }
    return null;
  };

  const exportCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.error("Não há lançamentos para exportar.");
      return;
    }

    const headers = ["Data", "Conta", "Tipo", "Categoria", "Descricao", "Valor (R$)", "Metodo", "Comprovante"];
    const rows = filteredTransactions.map((tx) => [
      new Date(tx.created_at).toLocaleString("pt-BR"),
      tx.account_type === "conta_corrente_loja" ? "Conta Corrente Loja" : "Tronco de Beneficencia",
      tx.type,
      tx.category,
      `"${(tx.description || '').replace(/"/g, '""')}"`,
      tx.amount,
      tx.payment_method,
      tx.proof_code
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `financeiro_loja_maconica_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Balancete CSV exportado com sucesso!");
  };

  const exportPDF = async () => {
    if (filteredTransactions.length === 0) {
      toast.error("Não há lançamentos para exportar.");
      return;
    }
    // Usamos jsPDF + autoTable (mais confiável que html2canvas em ambientes headless)
    const { jsPDF } = await import("jspdf");
    const autoTableMod = await import("jspdf-autotable");
    const autoTable = autoTableMod.default || autoTableMod.autoTable;

    const formatBR = (n) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const periodoStr = dateFrom || dateTo
      ? `Período: ${dateFrom ? new Date(dateFrom+"T00:00").toLocaleDateString("pt-BR") : "início"} até ${dateTo ? new Date(dateTo+"T00:00").toLocaleDateString("pt-BR") : "hoje"}`
      : "Todos os lançamentos";
    const totalRec = filteredTransactions.filter(t => t.type === "receita").reduce((s, t) => s + (t.amount || 0), 0);
    const totalDesp = filteredTransactions.filter(t => t.type === "despesa").reduce((s, t) => s + (t.amount || 0), 0);
    const saldo = totalRec - totalDesp;

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Cabeçalho
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.setTextColor(184, 134, 11); // dourado #B8860B
    doc.text("Extrato Financeiro — Tesouraria Maçônica", pageWidth / 2, 18, { align: "center" });
    doc.setDrawColor(184, 134, 11);
    doc.setLineWidth(0.6);
    doc.line(14, 22, pageWidth - 14, 22);
    doc.setFontSize(10);
    doc.setFont("times", "italic");
    doc.setTextColor(90, 90, 90);
    doc.text(periodoStr, pageWidth / 2, 28, { align: "center" });

    // Tabela
    const body = filteredTransactions.map((tx) => {
      const dt = tx.payment_date
        ? new Date(tx.payment_date + "T00:00").toLocaleDateString("pt-BR")
        : (tx.created_at ? new Date(tx.created_at).toLocaleDateString("pt-BR") : "-");
      const cat = (tx.category || "-").substring(0, 30);
      const desc = (tx.description || "").substring(0, 70);
      const sign = tx.type === "receita" ? "+" : "-";
      const valor = `${sign} R$ ${formatBR(tx.amount)}`;
      const status = tx.reversed_by ? " (estornado)" : tx.reverses ? " (estorno)" : "";
      return [dt, cat, desc + status, valor];
    });

    autoTable(doc, {
      head: [["Data", "Categoria", "Descrição", "Valor"]],
      body,
      startY: 34,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: [184, 134, 11], textColor: [255, 255, 255], fontStyle: "bold", halign: "left" },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 38 },
        2: { cellWidth: "auto" },
        3: { cellWidth: 30, halign: "right", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 3) {
          const raw = data.cell.raw || "";
          if (typeof raw === "string" && raw.startsWith("+")) {
            data.cell.styles.textColor = [5, 150, 105]; // verde
          } else if (typeof raw === "string" && raw.startsWith("-")) {
            data.cell.styles.textColor = [220, 38, 38]; // vermelho
          }
        }
      },
      margin: { left: 14, right: 14 },
    });

    // Totais
    const finalY = (doc.lastAutoTable?.finalY || 34) + 6;
    doc.setDrawColor(184, 134, 11);
    doc.setLineWidth(0.4);
    doc.line(14, finalY, pageWidth - 14, finalY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(5, 150, 105);
    doc.text(`Receitas: R$ ${formatBR(totalRec)}`, 16, finalY + 6);
    doc.setTextColor(220, 38, 38);
    doc.text(`Despesas: R$ ${formatBR(totalDesp)}`, pageWidth / 2, finalY + 6, { align: "center" });
    doc.setTextColor(saldo >= 0 ? 5 : 220, saldo >= 0 ? 150 : 38, saldo >= 0 ? 105 : 38);
    doc.text(`Saldo: R$ ${formatBR(saldo)}`, pageWidth - 16, finalY + 6, { align: "right" });

    // Rodapé
    doc.setFont("times", "italic");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Emitido em ${new Date().toLocaleString("pt-BR")} • ${filteredTransactions.length} lançamento(s)`,
      pageWidth / 2,
      finalY + 14,
      { align: "center" }
    );

    doc.save(`Extrato_Tesouraria_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF exportado!");
  };

  return (
    <div className="space-y-6" data-testid="lodge-finance-section">
      
      {/* Header & Main Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Tesouraria Maçônica</h2>
          <p className="text-xs text-slate-400">
            Conta Corrente da Loja, Tronco de Beneficência, Centros de Custo e Descontos por Pontualidade
          </p>
        </div>

        <div className="flex items-center space-x-2.5">
          <button
            data-testid="btn-open-cost-center-modal"
            onClick={onOpenCostCenterModal}
            className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-slate-200 border border-slate-700 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all"
          >
            <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>+ Centro de Custo</span>
          </button>

          {onOpenTransferModal && (
            <button
              data-testid="btn-open-transfer-modal"
              onClick={onOpenTransferModal}
              className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] border border-slate-700 text-slate-200 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shrink-0"
              title="Transferir dinheiro entre contas"
            >
              <ArrowLeftRight className="w-4 h-4 text-[#D4AF37]" />
              <span>Transferir</span>
            </button>
          )}

          <button
            data-testid="btn-open-new-finance-modal"
            onClick={onOpenNewFinanceModal}
            className="flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)] shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Lançamento</span>
          </button>
        </div>
      </div>

      {/* Credit Alert Banner */}
      {creditAlerts && creditAlerts.count > 0 && !creditAlertDismissed && (
        <div className="p-3 rounded-xl border border-purple-500/40 bg-gradient-to-r from-purple-500/10 to-fuchsia-500/5 flex items-start gap-3" data-testid="credit-alert-banner">
          <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-300 shrink-0">
            <Coins className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="font-bold text-purple-200 text-sm">
                  {creditAlerts.count} Irmão{creditAlerts.count > 1 ? "s" : ""} com crédito acumulado acima de {formatCurrency(creditAlerts.threshold)}
                </span>
                <span className="ml-2 text-[10px] text-slate-400 font-mono">
                  Total: <span className="text-purple-300 font-bold">{formatCurrency(creditAlerts.total_credit)}</span>
                </span>
              </div>
              <button
                data-testid="btn-dismiss-credit-alert"
                onClick={() => setCreditAlertDismissed(true)}
                className="text-slate-500 hover:text-white text-xs"
              >
                ✕
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
              {creditAlerts.members.slice(0, 10).map((m) => (
                <button
                  key={m.id}
                  onClick={() => onViewMemberStatement && onViewMemberStatement(m.id)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/40 text-purple-200 hover:bg-purple-500/25 transition"
                  data-testid={`credit-alert-member-${m.id}`}
                >
                  {m.name}: <span className="font-mono font-bold">{formatCurrency(m.credit_balance)}</span>
                </button>
              ))}
              {creditAlerts.members.length > 10 && (
                <span className="px-2 py-0.5 text-slate-400">+{creditAlerts.members.length - 10} outros...</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subtabs Bar */}
      <div className="flex overflow-x-auto border-b border-slate-800 space-x-2 sm:space-x-4">
        <button
          data-testid="subtab-finance-geral"
          onClick={() => setActiveSubTab("geral")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "geral"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Coins className="w-4 h-4" />
          <span>Conta Corrente & Tronco</span>
        </button>

        <button
          data-testid="subtab-finance-plano-contas"
          onClick={() => setActiveSubTab("plano_contas")}
          className={`flex shrink-0 items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "plano_contas"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          <span>Plano de Contas</span>
        </button>

        <button
          data-testid="subtab-finance-centros-custo"
          onClick={() => setActiveSubTab("centros_custo")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "centros_custo"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Centros de Custo ({costCenters?.length || 0})</span>
        </button>

        <button
          data-testid="subtab-finance-mensalidades"
          onClick={() => setActiveSubTab("mensalidades")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "mensalidades"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Receipt className="w-4 h-4" />
          <span>Capitações & Descontos</span>
        </button>

        <button
          data-testid="subtab-finance-eventos"
          onClick={() => setActiveSubTab("eventos")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "eventos"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Gift className="w-4 h-4" />
          <span>Eventos & Cobranças Avulsas</span>
        </button>

        <button
          data-testid="subtab-finance-manuais"
          onClick={() => setActiveSubTab("manuais")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "manuais"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Lançamentos Manuais</span>
        </button>

        <button
          data-testid="subtab-finance-saldos"
          onClick={() => setActiveSubTab("saldos")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "saldos"
              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <UserCheck className="w-4 h-4" />
          <span>Saldo por Pessoa</span>
        </button>

        <button
          data-testid="subtab-finance-pendentes"
          onClick={() => setActiveSubTab("pendentes")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "pendentes"
              ? "border-amber-400 text-amber-400 bg-amber-400/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <Tag className="w-4 h-4" />
          <span>Pendentes</span>
          {pendingCount > 0 && (
            <span className="ml-1 bg-amber-500 text-slate-950 text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {pendingCount}
            </span>
          )}
        </button>

        <button
          data-testid="subtab-finance-filantropia"
          onClick={() => setActiveSubTab("filantropia")}
          className={`flex items-center space-x-2 py-3 px-4 text-xs uppercase font-bold tracking-wider border-b-2 transition-all ${
            activeSubTab === "filantropia"
              ? "border-purple-400 text-purple-300 bg-purple-400/5"
              : "border-transparent text-slate-400 hover:text-white"
          }`}
        >
          <HeartHandshake className="w-4 h-4" />
          <span>Filantropia</span>
        </button>
      </div>

      {/* TAB 1: GERAL (CONTA CORRENTE & TRONCO) */}
      {activeSubTab === "geral" && (
        <div className="space-y-6">
          {/* Account Balances Cards — Dinâmicos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-lg text-white">Contas Bancárias & Aplicações</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {bankAccounts.filter(b => b.is_active !== false).length} conta(s) ativa(s) • Cadastre C/C, Poupança, Aplicações, PIX e Caixa Físico
                </p>
              </div>
              {onOpenBankAccountModal && (
                <button
                  data-testid="btn-open-bank-account-modal"
                  onClick={onOpenBankAccountModal}
                  className="flex items-center gap-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-slate-200 border border-slate-700 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all"
                >
                  <Landmark className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span>+ Adicionar Banco</span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bankAccounts.filter(b => b.is_active !== false).map((ba) => {
                const isTronco = ba.account_kind === "tronco";
                const IconComp = isTronco ? HeartHandshake : Coins;
                const c = ba.color || "#D4AF37";
                const balance = ba.current_balance != null ? ba.current_balance : 0;
                const kindLabel = {
                  corrente: "Conta Corrente",
                  poupanca: "Poupança",
                  aplicacao: "Aplicação",
                  pix: "PIX",
                  caixa_fisico: "Caixa Físico",
                  tronco: "Tronco de Beneficência",
                }[ba.account_kind] || "Conta";
                const bankLine = [ba.bank_name, ba.branch && `Ag ${ba.branch}`, ba.account_number && `C ${ba.account_number}`]
                  .filter(Boolean).join(" • ");
                return (
                  <div
                    key={ba.id}
                    className="luxury-card p-5 border-l-4"
                    style={{ borderLeftColor: c }}
                    data-testid={`bank-account-card-${ba.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${c}22`, color: c }}
                        >
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider block truncate">{ba.name}</span>
                          <span className="text-[10px] text-slate-500 block truncate">{kindLabel}{bankLine ? " • " + bankLine : ""}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {ba.is_locked && (
                          <span
                            data-testid={`bank-locked-badge-${ba.id}`}
                            title="Conta imutável — fundo filantrópico protegido"
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 font-bold uppercase tracking-wider"
                          >
                            Imutável
                          </span>
                        )}
                        {onEditBankAccount && !ba.is_locked && (
                          <button
                            data-testid={`btn-edit-bank-account-${ba.id}`}
                            onClick={() => onEditBankAccount(ba)}
                            className="p-1 rounded text-slate-500 hover:text-[#D4AF37]"
                            title="Editar"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {onDeleteBankAccount && !ba.is_default && !ba.is_locked && (
                          <button
                            data-testid={`btn-delete-bank-account-${ba.id}`}
                            onClick={() => onDeleteBankAccount(ba.id)}
                            className="p-1 rounded text-slate-500 hover:text-rose-400"
                            title="Excluir"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <h3
                      className="text-2xl font-serif font-bold font-mono"
                      style={{ color: balance < 0 ? "#F87171" : "#FFFFFF" }}
                      data-testid={`bank-account-balance-${ba.id}`}
                    >
                      {formatCurrency(balance)}
                    </h3>
                    {ba.is_default && (
                      <span className="inline-block mt-1 text-[9px] uppercase tracking-widest text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded">Conta Padrão</span>
                    )}
                  </div>
                );
              })}
              {bankAccounts.filter(b => b.is_active !== false).length === 0 && (
                <div className="col-span-full luxury-card p-6 text-center text-slate-400 text-sm">
                  Nenhuma conta cadastrada. Clique em "+ Adicionar Banco" para começar.
                </div>
              )}
            </div>
          </div>

          {/* Filter Bar & Export */}
          <div className="luxury-card p-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="input-search-finance"
                type="text"
                placeholder="Buscar por descrição, categoria ou comprovante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#080C14] text-xs text-white placeholder-slate-500 pl-9 pr-3 py-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37]"
              />
            </div>

            <select
              data-testid="select-account-type-filter"
              value={accountTypeFilter}
              onChange={(e) => setAccountTypeFilter(e.target.value)}
              className="bg-[#080C14] text-xs text-white px-3 py-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37]"
            >
              <option value="all">Todas as Contas</option>
              {bankAccounts.map((ba) => (
                <option key={ba.id} value={`ba:${ba.id}`}>{ba.name}</option>
              ))}
            </select>

            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">De:</label>
              <input
                data-testid="input-finance-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-[#080C14] text-xs text-white px-2 py-2 rounded-lg border border-slate-700 focus:border-[#D4AF37] font-mono"
              />
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Até:</label>
              <input
                data-testid="input-finance-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-[#080C14] text-xs text-white px-2 py-2 rounded-lg border border-slate-700 focus:border-[#D4AF37] font-mono"
              />
              {(dateFrom || dateTo) && (
                <button
                  data-testid="btn-clear-date-filter"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  title="Limpar filtro"
                  className="text-[10px] text-slate-500 hover:text-white px-1"
                >×</button>
              )}
            </div>

            <button
              data-testid="btn-export-finance-csv"
              onClick={exportCSV}
              className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-emerald-400/40 font-semibold px-3 py-2.5 rounded-xl text-xs transition-all"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
              <span>Excel/CSV</span>
            </button>
            <button
              data-testid="btn-export-finance-pdf"
              onClick={exportPDF}
              className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-[#D4AF37]/40 font-semibold px-3 py-2.5 rounded-xl text-xs transition-all"
            >
              <FileText className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span>PDF</span>
            </button>
          </div>

          {/* Transactions Table */}
          <div className="luxury-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="table-finance-records">
                <thead>
                  <tr className="text-xs text-slate-400 uppercase bg-[#080C14]/80 border-b border-slate-800">
                    <th className="py-3.5 px-4">Data</th>
                    <th className="py-3.5 px-4">Conta</th>
                    <th className="py-3.5 px-4">Categoria / Subcategoria</th>
                    <th className="py-3.5 px-4">Descrição</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Recibo</th>
                    <th className="py-3.5 px-4 text-right">Valor</th>
                    <th className="py-3.5 px-4 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {filteredTransactions.map((tx) => {
                    const isReceita = tx.type === "receita";
                    const status = tx.status || "pago";
                    const isPago = status === "pago";

                    return (
                      <tr key={tx.id} data-testid={`finance-row-${tx.id}`} className="hover:bg-white/5 transition-colors">
                        
                        <td className="py-3.5 px-4 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(tx.created_at).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                          })}
                        </td>

                        <td className="py-3.5 px-4 text-xs">
                          <span className={`px-2 py-0.5 rounded-full font-semibold ${
                            tx.account_type === "conta_corrente_loja"
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                              : "bg-purple-500/10 text-purple-400 border border-purple-500/30"
                          }`}>
                            {tx.account_type === "conta_corrente_loja" ? "C/C" : "Tronco"}
                          </span>
                        </td>

                        <td className="py-3.5 px-4 text-xs text-white whitespace-nowrap">
                          <div className="font-medium">{tx.category}</div>
                          {tx.subcategory && (
                            <div className="text-[10px] text-slate-400">{tx.subcategory}</div>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-xs text-slate-300 min-w-[280px] max-w-md" title={tx.description}>
                          <div className="whitespace-normal break-words leading-snug">
                            {tx.description}
                          </div>
                          {tx.tags && tx.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {tx.tags.map((t) => (
                                <span key={t} className="text-[9px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-mono">
                                  #{t}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-xs">
                          {isPago ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                              <BadgeCheck className="w-3 h-3" />
                              Pago
                            </span>
                          ) : (
                            <button
                              data-testid={`btn-mark-paid-${tx.id}`}
                              onClick={() => onMarkAsPaid && onMarkAsPaid(tx)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500 hover:text-slate-950 transition-all"
                              title="Marcar como pago (emite recibo)"
                            >
                              <Clock className="w-3 h-3" />
                              Pendente
                            </button>
                          )}
                        </td>

                        <td className="py-3.5 px-4 text-[10px] font-mono text-[#D4AF37]">
                          {tx.receipt_number || "—"}
                        </td>

                        <td className={`py-3.5 px-4 text-right font-mono font-bold whitespace-nowrap ${
                          isReceita ? "text-emerald-400" : "text-rose-400"
                        }`}>
                          {isReceita ? "+" : "-"} {formatCurrency(tx.amount)}
                        </td>

                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          {tx.receipt_id && (
                            <button
                              data-testid={`btn-view-receipt-${tx.id}`}
                              onClick={() => onViewReceipt && onViewReceipt(tx)}
                              title="Ver / Imprimir Recibo"
                              className="p-1.5 rounded-lg text-[#D4AF37] hover:bg-[#D4AF37]/10 mr-1"
                            >
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {tx.reversed_by ? (
                            <span
                              title={`Estornado — ${tx.reversal_reason || ""}`}
                              className="px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[10px] uppercase tracking-wider font-bold"
                            >
                              Estornado
                            </span>
                          ) : tx.reverses ? (
                            <span
                              title="Lançamento de estorno (compensação)"
                              className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] uppercase tracking-wider font-bold"
                            >
                              Estorno
                            </span>
                          ) : (
                            <>
                              <button
                                data-testid={`btn-reverse-tx-${tx.id}`}
                                onClick={() => onReverseTransaction && onReverseTransaction(tx)}
                                title="Estornar Lançamento (cria linha compensatória)"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                              {tx.batch_id && tx.status === "pendente" && (
                                <button
                                  data-testid={`btn-cancel-batch-${tx.id}`}
                                  onClick={() => onCancelBatch && onCancelBatch(tx)}
                                  title="Cancelar TODAS as parcelas pendentes deste cronograma"
                                  className="ml-1 p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === "plano_contas" && (
        <FinanceChartOfAccounts />
      )}

      {/* TAB 2: CENTROS DE CUSTO */}
      {activeSubTab === "centros_custo" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif font-bold text-lg text-white">
              Centros de Custo da Oficina
            </h3>
            <button
              onClick={onOpenCostCenterModal}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider"
            >
              + Adicionar Centro
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {(costCenters || []).map((cc) => (
              <div key={cc.id} className="luxury-card p-5 space-y-3 hover:border-[#D4AF37]/60 transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono font-bold text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-0.5 rounded border border-[#D4AF37]/30">
                      {cc.code}
                    </span>
                    <div className="flex items-center gap-1">
                      {onEditCostCenter && (
                        <button
                          data-testid={`btn-edit-cost-center-${cc.id}`}
                          onClick={() => onEditCostCenter(cc)}
                          className="p-1 rounded text-slate-500 hover:text-[#D4AF37]"
                          title="Editar Centro de Custo"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        data-testid={`btn-delete-cost-center-${cc.id}`}
                        onClick={() => onDeleteCostCenter(cc.id)}
                        className="p-1 rounded text-slate-500 hover:text-rose-400"
                        title="Excluir Centro de Custo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <h4 className="font-bold text-sm text-white">{cc.name}</h4>
                  <p className="text-xs text-slate-400 mt-1">{cc.description || "Centro de custeio administrativo"}</p>
                </div>

                <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Orçamento Mensal:</span>
                  <span className="font-mono font-bold text-emerald-400">{formatCurrency(cc.monthly_budget)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: MENSALIDADES & DESCONTO DE PONTUALIDADE */}
      {activeSubTab === "mensalidades" && (
        <div className="space-y-6">
          
          {/* Rules Configuration Box */}
          <div className="luxury-card p-6 border-l-4 border-l-[#D4AF37] space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-base text-white flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                  <span>Regras Financeiras da Gestão</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Capitação padrão, desconto de pontualidade e descontos vitalícios (Emérito / Provecto)
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  data-testid="btn-show-settings-history"
                  onClick={() => setShowHistory(!showHistory)}
                  className="text-xs text-slate-400 hover:text-[#D4AF37] font-semibold flex items-center space-x-1"
                >
                  <History className="w-3.5 h-3.5" />
                  <span>{showHistory ? "Ocultar Histórico" : "Ver Histórico"}</span>
                </button>
                <button
                  onClick={() => setIsEditingSettings(!isEditingSettings)}
                  className="text-xs text-[#D4AF37] hover:underline font-semibold flex items-center space-x-1"
                >
                  <Sliders className="w-3.5 h-3.5" />
                  <span>{isEditingSettings ? "Fechar Edição" : "Editar Regras"}</span>
                </button>
              </div>
            </div>

            {showHistory && (
              <div data-testid="settings-history-panel" className="p-4 rounded-xl bg-[#080C14] border border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-3">
                  Histórico de Regras — Snapshot de valores anteriores
                </div>
                {historyLoading && <div className="text-xs text-slate-500 italic">Carregando…</div>}
                {!historyLoading && history.length === 0 && (
                  <div className="text-xs text-slate-500 italic">Nenhuma alteração registrada ainda.</div>
                )}
                {!historyLoading && history.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] text-slate-300">
                      <thead>
                        <tr className="text-left text-slate-400 border-b border-slate-800">
                          <th className="py-2 pr-3">Arquivado em</th>
                          <th className="py-2 pr-3">Vigência</th>
                          <th className="py-2 pr-3 text-right">Capitação</th>
                          <th className="py-2 pr-3 text-right">Pontualidade</th>
                          <th className="py-2 pr-3 text-right">Emérito</th>
                          <th className="py-2 pr-3 text-right">Provecto</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {history.map((h, i) => {
                          const dt = (s) => s ? new Date(s).toLocaleDateString("pt-BR") : "—";
                          return (
                            <tr key={h.id || i} className="border-b border-slate-900">
                              <td className="py-2 pr-3">{dt(h.archived_at)}</td>
                              <td className="py-2 pr-3 text-slate-400">{dt(h.valid_from)} → {dt(h.valid_until)}</td>
                              <td className="py-2 pr-3 text-right text-white">{formatCurrency(h.default_monthly_fee)}</td>
                              <td className="py-2 pr-3 text-right text-emerald-400">- {formatCurrency(h.prompt_discount_value)}</td>
                              <td className="py-2 pr-3 text-right text-[#D4AF37]">
                                - {formatCurrency(h.emerito_discount_value || 0)}
                                {h.emerito_effective_from && <span className="block text-[9px] text-slate-500 italic">a partir de {dt(h.emerito_effective_from)}</span>}
                              </td>
                              <td className="py-2 pr-3 text-right text-emerald-400">
                                - {formatCurrency(h.provecto_discount_value || 0)}
                                {h.provecto_effective_from && <span className="block text-[9px] text-slate-500 italic">a partir de {dt(h.provecto_effective_from)}</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {isEditingSettings ? (
              <form onSubmit={handleSaveSettings} className="p-4 rounded-xl bg-[#080C14] border border-slate-800 space-y-4">
                {/* SEÇÃO 1: Pontualidade (CONDICIONAL) */}
                <div className="p-3 rounded-lg bg-gradient-to-r from-[#131C2E] to-[#0F1522] border border-slate-800">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" />
                    Pontualidade (Desconto Condicional)
                  </div>
                  <p className="text-[10px] text-slate-500 italic mb-3">
                    Desconto aplicado <b>somente</b> quando o Irmão paga a capitação até o dia de vencimento.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block text-slate-400 mb-1">Dia do Vencimento (1 a 31)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={dueDay}
                      onChange={(e) => setDueDay(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1">Desconto de Pontualidade (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={discountVal}
                      onChange={(e) => setDiscountVal(e.target.value)}
                      className="w-full bg-[#131C2E] text-[#D4AF37] font-bold p-2 rounded-lg border border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1">Capitação Padrão (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={defaultFee}
                      onChange={(e) => setDefaultFee(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700"
                    />
                  </div>
                </div>
                </div>

                {/* ▼▼▼ Composição/Rateio da Capitação — logo abaixo do valor ▼▼▼ */}
                <RateioRulesCard
                  costCenters={costCenters || []}
                  canManage={true}
                  capitationValue={parseFloat(defaultFee) || 0}
                />

                {/* SEÇÃO 2: Período de Gestão */}
                <div className="p-3 rounded-lg bg-gradient-to-r from-[#131C2E] to-[#0F1522] border border-slate-800">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" />
                    Período da Gestão Maçônica
                  </div>
                  <p className="text-[10px] text-slate-500 italic mb-3">Define o ciclo de 12 meses da gestão vigente.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block text-slate-400 mb-1">Início da Gestão — Mês (1 a 12)</label>
                    <select
                      data-testid="input-gestao-start-month"
                      value={gestaoStartMonth}
                      onChange={(e) => setGestaoStartMonth(e.target.value)}
                      className="w-full bg-[#131C2E] text-[#D4AF37] font-bold p-2 rounded-lg border border-slate-700"
                    >
                      {["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"].map((m, i) => (
                        <option key={i} value={i + 1}>{i + 1} — {m}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1 italic">Padrão maçônico: Julho. Gestão vai deste mês até o mesmo mês -1 do ano seguinte (12 meses).</p>
                  </div>
                  <div>
                    <label className="block text-slate-400 mb-1">Início da Gestão — Ano</label>
                    <input
                      data-testid="input-gestao-start-year"
                      type="number"
                      min="2020"
                      max="2100"
                      value={gestaoStartYear}
                      onChange={(e) => setGestaoStartYear(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono"
                    />
                    <p className="text-[10px] text-slate-500 mt-1 italic">Ex.: 2026 → gestão 1/7/2026 até 30/6/2027.</p>
                  </div>
                </div>
                </div>

                {/* SEÇÃO 3: Descontos Vitalícios (ADQUIRIDOS) */}
                <div className="p-3 rounded-lg bg-gradient-to-br from-[#1a1a10] via-[#0F1522] to-[#0a1a12] border border-emerald-500/20">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1 flex items-center gap-2">
                    <Crown className="w-3 h-3" />
                    Descontos Vitalícios (Adquiridos — Sempre Aplicados)
                  </div>
                  <p className="text-[10px] text-slate-400 italic mb-3">
                    Direito vitalício conquistado pelo Irmão. Aplicado <b>independentemente</b> de o pagamento ser à vista ou em atraso — não tem relação com o desconto de pontualidade acima.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-lg border border-slate-800 bg-[#0F1522] space-y-2">
                    <label className="block text-slate-400 mb-1 flex items-center gap-1">
                      <Crown className="w-3 h-3 text-[#D4AF37]" />
                      Desconto Vitalício — Maçom Emérito (R$)
                    </label>
                    <input
                      data-testid="input-emerito-discount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={emeritoDisc}
                      onChange={(e) => setEmeritoDisc(e.target.value)}
                      className="w-full bg-[#131C2E] text-[#D4AF37] font-bold p-2 rounded-lg border border-slate-700"
                    />
                    <label className="block text-slate-400 mt-2 mb-1">Vigência a partir de</label>
                    <input
                      data-testid="input-emerito-effective-from"
                      type="date"
                      value={emeritoEffFrom || ""}
                      onChange={(e) => setEmeritoEffFrom(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono"
                    />
                    <p className="text-[10px] text-slate-500 italic">
                      O desconto só vale para mensalidades com vencimento igual ou posterior a esta data. Deixe em branco para valer sempre.
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border border-slate-800 bg-[#0F1522] space-y-2">
                    <label className="block text-slate-400 mb-1 flex items-center gap-1">
                      <Crown className="w-3 h-3 text-emerald-400" />
                      Desconto Vitalício — Maçom Provecto (R$)
                    </label>
                    <input
                      data-testid="input-provecto-discount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={provectoDisc}
                      onChange={(e) => setProvectoDisc(e.target.value)}
                      className="w-full bg-[#131C2E] text-emerald-400 font-bold p-2 rounded-lg border border-slate-700"
                    />
                    <label className="block text-slate-400 mt-2 mb-1">Vigência a partir de</label>
                    <input
                      data-testid="input-provecto-effective-from"
                      type="date"
                      value={provectoEffFrom || ""}
                      onChange={(e) => setProvectoEffFrom(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 font-mono"
                    />
                    <p className="text-[10px] text-slate-500 italic">
                      O desconto só vale para mensalidades com vencimento igual ou posterior a esta data. Deixe em branco para valer sempre.
                    </p>
                  </div>
                </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsEditingSettings(false)}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-[#D4AF37] text-slate-950 font-bold px-4 py-1.5 rounded-lg text-xs"
                  >
                    Salvar Regras
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <span className="text-slate-400 block text-[10px] uppercase">Dia de Vencimento</span>
                  <span className="text-base font-bold text-white font-mono">Todo dia {dueDay}</span>
                </div>
                <div className="p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <span className="text-slate-400 block text-[10px] uppercase">Desconto até o Vencimento</span>
                  <span className="text-base font-bold text-emerald-400 font-mono">- {formatCurrency(discountVal)}</span>
                </div>
                <div className="p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <span className="text-slate-400 block text-[10px] uppercase">Valor Líquido c/ Desconto</span>
                  <span className="text-base font-bold text-[#D4AF37] font-mono">{formatCurrency(defaultFee - discountVal)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Members Capitation Directory */}
          <div className="luxury-card p-6 space-y-4">
            <h3 className="font-serif font-bold text-base text-white">
              Extrato & Situação de Capitações dos Irmãos
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase">
                    <th className="py-2.5 px-3">Irmão do Quadro</th>
                    <th className="py-2.5 px-3">CIM</th>
                    <th className="py-2.5 px-3">Grau</th>
                    <th className="py-2.5 px-3 text-right">Capitação</th>
                    <th className="py-2.5 px-3 text-right">c/ Desconto até dia {dueDay}</th>
                    <th className="py-2.5 px-3 text-center">Extrato Individual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {members.map((m) => (
                    <tr key={m.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-2.5 px-3 font-semibold text-white">
                        {m.name}
                      </td>
                      <td className="py-2.5 px-3 text-slate-400 font-mono">
                        {m.cim}
                      </td>
                      <td className="py-2.5 px-3 text-slate-300">
                        {m.degree}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-300">
                        {formatCurrency(m.monthly_fee)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-400">
                        {formatCurrency(m.monthly_fee - discountVal)}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          data-testid={`btn-open-statement-${m.id}`}
                          onClick={() => onViewMemberStatement(m.id)}
                          className="bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-slate-950 font-bold px-3 py-1 rounded-lg text-xs transition-all"
                        >
                          Ver Extrato & Baixa
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* TAB 4: EVENTOS & COBRANÇAS AVULSAS */}
      {activeSubTab === "eventos" && (
        <EventsView
          members={members}
          costCenters={costCenters}
          formatCurrency={formatCurrency}
        />
      )}

      {activeSubTab === "manuais" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-serif text-lg text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#D4AF37]" />
                Lançamentos Manuais &amp; Rateios
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Ideal para aluguel do templo (semestre/trimestre), contribuição à Potência, ágapes e outras despesas fixas ou correções de fim de mês.
              </p>
            </div>
          </div>
          <InstallmentBatchForm
            members={members}
            contacts={contacts}
            costCenters={costCenters}
            onSuccess={() => {
              window.location.reload();
            }}
          />
        </div>
      )}

      {activeSubTab === "saldos" && (
        <BalancesByPersonView />
      )}

      {activeSubTab === "filantropia" && (
        <FilantropiaReportView />
      )}

      {activeSubTab === "pendentes" && (
        <div className="luxury-card p-6" data-testid="pending-view">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-serif font-bold text-lg text-white mb-1 flex items-center gap-2">
                <Tag className="w-5 h-5 text-amber-400" />
                Lançamentos Pendentes de Classificação
              </h3>
              <p className="text-xs text-slate-400">
                Movimentações registradas sem conta bancária ou categoria definida. Classifique-as para que sejam somadas nos saldos e relatórios.
              </p>
            </div>
            {onOpenImportStatement && (
              <button
                data-testid="btn-open-import-statement"
                onClick={onOpenImportStatement}
                className="flex items-center gap-2 bg-[#131C2E] hover:bg-[#1E293B] border border-slate-700 text-slate-200 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shrink-0"
                title="Importar arquivo OFX ou CSV do banco"
              >
                <Upload className="w-4 h-4 text-[#D4AF37]" />
                <span>Importar OFX/CSV</span>
              </button>
            )}
          </div>

          {pendingClassifyList.length === 0 && pendingPaymentList.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">
              <Tag className="w-10 h-10 mx-auto text-slate-600 mb-3" />
              Nenhum lançamento pendente. Você está em dia! 🎉
              <p className="text-xs text-slate-500 mt-2 italic">
                Marque como "Deixar pendente" para lançamentos sem conta definida, ou crie parcelas recorrentes que aguardam pagamento.
              </p>
            </div>
          ) : (
            <div className="space-y-6">

              {/* SEÇÃO 1 — Pendentes de Classificação */}
              {pendingClassifyList.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-amber-400" />
                    <h4 className="font-serif font-bold text-sm text-amber-300 uppercase tracking-wider">
                      Pendentes de Classificação ({pendingClassifyList.length})
                    </h4>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3 italic">
                    Lançamentos sem conta bancária ou categoria definida (ex: extrato importado, PIX indefinido).
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase tracking-wider">
                        <tr>
                          <th className="text-left py-2 px-3">Data</th>
                          <th className="text-left py-2 px-3">Descrição</th>
                          <th className="text-left py-2 px-3">Tipo</th>
                          <th className="text-right py-2 px-3">Valor</th>
                          <th className="text-right py-2 px-3">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingClassifyList.map((tx) => (
                          <tr key={tx.id} className="border-b border-slate-800/50 hover:bg-[#131C2E]" data-testid={`pending-tx-${tx.id}`}>
                            <td className="py-2.5 px-3 font-mono text-[10px] text-slate-300">
                              {tx.payment_date ? new Date(tx.payment_date + "T00:00").toLocaleDateString("pt-BR") : new Date(tx.created_at).toLocaleDateString("pt-BR")}
                            </td>
                            <td className="py-2.5 px-3 text-slate-200 max-w-md truncate">{tx.description}</td>
                            <td className="py-2.5 px-3">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                tx.type === "receita" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"
                              }`}>
                                {tx.type === "receita" ? "Receita" : "Despesa"}
                              </span>
                            </td>
                            <td className={`py-2.5 px-3 text-right font-mono font-bold ${tx.type === "receita" ? "text-emerald-400" : "text-rose-400"}`}>
                              {tx.type === "receita" ? "+" : "-"} {formatCurrency(tx.amount)}
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              {onClassifyTx && (
                                <button
                                  data-testid={`btn-classify-${tx.id}`}
                                  onClick={() => onClassifyTx(tx)}
                                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider"
                                >
                                  Classificar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SEÇÃO 2 — Pendentes de Pagamento */}
              {pendingPaymentList.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <h4 className="font-serif font-bold text-sm text-cyan-300 uppercase tracking-wider">
                        Pendentes de Pagamento ({pendingPaymentList.length})
                      </h4>
                      {overdueCount > 0 && (
                        <span
                          data-testid="pending-overdue-badge"
                          className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/50 font-bold uppercase tracking-wider flex items-center gap-1"
                          title={`${overdueCount} conta(s) já vencida(s)`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {overdueCount} Vencido{overdueCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="btn-filter-pending-all"
                        onClick={() => setShowOnlyOverdue(false)}
                        className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-lg border transition-all ${
                          !showOnlyOverdue
                            ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/50"
                            : "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-white"
                        }`}
                      >
                        Todos
                      </button>
                      <button
                        data-testid="btn-filter-pending-overdue"
                        onClick={() => setShowOnlyOverdue(true)}
                        disabled={overdueCount === 0}
                        className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          showOnlyOverdue
                            ? "bg-rose-500/25 text-rose-200 border-rose-500/60"
                            : "bg-[#131C2E] text-slate-400 border-slate-700 hover:text-rose-300"
                        }`}
                      >
                        Só Vencidos
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mb-3 italic">
                    Contas a pagar/receber, parcelas recorrentes e mensalidades ainda não quitadas. Marque como pago ou cancele.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase tracking-wider">
                        <tr>
                          <th className="text-left py-2 px-3">Vencimento</th>
                          <th className="text-left py-2 px-3">Descrição</th>
                          <th className="text-left py-2 px-3">Devedor / Credor</th>
                          <th className="text-left py-2 px-3">Categoria</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-right py-2 px-3">Valor</th>
                          <th className="text-right py-2 px-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingPaymentList
                          .filter((tx) => {
                            if (!showOnlyOverdue) return true;
                            const d = tx.due_date || tx.payment_date;
                            return d && d < new Date().toISOString().slice(0,10);
                          })
                          .slice()
                          .sort((a, b) => (a.due_date || a.payment_date || "").localeCompare(b.due_date || b.payment_date || ""))
                          .map((tx) => {
                            const ds = getDueStatus(tx);
                            const person = getTransactionPerson(tx);
                            const rowClass = ds.level === "overdue" ? "bg-rose-500/5" : ds.level === "soon" ? "bg-amber-500/5" : "";
                            return (
                              <tr key={tx.id} className={`border-b border-slate-800/50 hover:bg-[#131C2E] ${rowClass}`} data-testid={`payment-pending-${tx.id}`}>
                                <td className="py-2.5 px-3 font-mono text-[10px]">
                                  <div className={`${ds.level === "overdue" ? "text-rose-300 font-bold" : ds.level === "soon" ? "text-amber-300 font-semibold" : "text-slate-300"}`}>
                                    {tx.due_date ? new Date(tx.due_date + "T00:00").toLocaleDateString("pt-BR") : "-"}
                                  </div>
                                  <div className={`text-[9px] mt-0.5 ${ds.level === "overdue" ? "text-rose-400" : ds.level === "soon" ? "text-amber-400" : "text-slate-500"}`}>
                                    {ds.label}
                                  </div>
                                </td>
                                <td className="py-2.5 px-3 text-slate-200 max-w-md truncate">
                                  {tx.description}
                                  {tx.installment_number && tx.total_installments && (
                                    <span className="ml-2 text-[10px] text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded">
                                      {tx.installment_number}/{tx.total_installments}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 px-3 min-w-[180px]" data-testid={`pending-person-${tx.id}`}>
                                  {person ? (
                                    <>
                                      <div className="font-semibold text-white">{person.name}</div>
                                      <div className="mt-0.5 text-[9px] uppercase tracking-wider text-cyan-300">{person.detail}</div>
                                    </>
                                  ) : <span className="text-[10px] italic text-slate-600">Não vinculado</span>}
                                </td>
                                <td className="py-2.5 px-3 text-slate-400 text-[10px]">{tx.category || "-"}</td>
                                <td className="py-2.5 px-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    tx.type === "receita" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"
                                  }`}>
                                    {tx.type === "receita" ? "A Receber" : "A Pagar"}
                                  </span>
                                </td>
                                <td className={`py-2.5 px-3 text-right font-mono font-bold ${tx.type === "receita" ? "text-emerald-400" : "text-rose-400"}`}>
                                  {tx.type === "receita" ? "+" : "-"} {formatCurrency(tx.amount)}
                                </td>
                                <td className="py-2.5 px-3 text-right">
                                  <div className="flex justify-end items-center gap-1">
                                    {onEditPendingBank && (
                                      <button
                                        data-testid={`btn-edit-bank-${tx.id}`}
                                        onClick={() => onEditPendingBank(tx)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 border border-slate-800"
                                        title="Alterar banco desta parcela pendente"
                                      >
                                        <Landmark className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {onMarkPaid && (
                                      <button
                                        data-testid={`btn-mark-paid-${tx.id}`}
                                        onClick={() => onMarkPaid(tx)}
                                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider"
                                        title={tx.type === "receita" ? "Dar baixa no recebimento" : "Dar baixa no pagamento"}
                                      >
                                        {tx.type === "receita" ? "Receber" : "Pagar"}
                                      </button>
                                    )}
                                    {onCancelPending && (
                                      <button
                                        data-testid={`btn-cancel-pending-${tx.id}`}
                                        onClick={() => onCancelPending(tx)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400"
                                        title="Cancelar (excluir esta parcela pendente)"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}

    </div>
  );
};
